import { Button, message, Table } from 'antd';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { selectBuyer } from 'containers/MainLayout/selectors';
import { GQuery, useQuerySaga } from 'hooks/useQuerySaga';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styles/styled-components';
import translations from 'translations';
import { ApplicationRootState } from 'types';
import formDateTimeFromGraphQL from 'utils/formatDateTime';
import { referralContactRequestHistory } from 'utils/query';
import CopyIcon from './copyIcon.svg';
import messages from './messages';
import PolicyModal from './PolicyModal';
import { ReferralContactRequest } from 'types/schema';
import formatMoney from 'utils/formatMoney';
import { Link } from 'react-router-dom';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 0 0 24px 16px;
  @media (max-width: 991px) {
    margin: 0 16px;
  }
`;

const TitleText = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #2f382c;
  margin: 0;
  @media (max-width: 767px) {
    margin-top: 12px;
  }
`;

const HeaderCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 16px;
  display: grid;
  grid-template-columns: min-content 1fr;
  align-items: center;
  gap: 16px;
  row-gap: 4px;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.06);
`;

const HeaderIconBox = styled.div`
  width: 40px;
  height: 40px;
  background: #43a047;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  @media (min-width: 768px) {
    grid-area: 1 / 1 / 3 / 2;
  }
`;

const HeaderMainText = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #2f382c;
  margin: 0;
  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const HeaderSubText = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #2f382c;
  margin: 0;
  grid-area: 2 / 2 / 3 / 3;
  @media (max-width: 767px) {
    font-size: 12px;
    grid-area: 2 / 1 / 3 / 3;
  }
`;

const TextLink = styled.span`
  cursor: pointer;
  color: #136fd8;
  font-weight: 500;
`;

const TextVsLink = styled(Link)`
  cursor: pointer;
  color: #136fd8;
  font-weight: 500;
  &:hover {
    color: #136fd8;
    opacity: 0.9;
  }
`;

const BottomSection = styled.div`
  display: flex;
  gap: 16px;
  @media (max-width: 991px) {
    flex-direction: column;
  }
`;

const Card = styled.div`
  flex: 1;
  background: #ffffff;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.06);
`;

const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #2f382c;
  margin: 0;
  @media (max-width: 991px) {
    font-weight: 500;
  }
`;

const CodeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CodeRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CodeLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #757d73;
`;

const CopyRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const CodeBox = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #2f382c;
  background: #ffffff;
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 40px;
`;

const CopyButton = styled(Button)`
  height: 40px;
  flex-shrink: 0;
  background: #43a047;
  border-color: #43a047;
  border-radius: 8px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  &:hover,
  &:focus {
    background: #388e3c !important;
    border-color: #388e3c !important;
    color: #ffffff !important;
  }
`;

const ShareButton = styled(Button)`
  height: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 500;
  @media (min-width: 992px) {
    display: none;
  }
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StepItem = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const StepBadge = styled.div`
  width: 28px;
  height: 28px;
  min-width: 28px;
  background: #f0f9f1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #43a047;
`;

const StepContentGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StepTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2f382c;
`;

const StepDescription = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #2f382c;
`;

const ReferralLink = styled.div`
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
`;

const ReferralListCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ReferralListTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #2f382c;
  margin: 0;
  @media (max-width: 991px) {
    font-weight: 500;
  }
`;

const StyledTable = styled(Table)`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  .ant-table-thead > tr > th {
    background: #f5f5f4;
    font-size: 14px;
    font-weight: 600;
    color: #2f382c;
    padding: 12px 16px;
    // border-bottom: 1px solid #d5d7d5;
  }
  .ant-table-tbody > tr > td {
    font-size: 14px;
    color: #2f382c;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
  }
  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }
  @media (max-width: 767px) {
    display: none;
  }
` as typeof Table;

const MobileList = styled.div`
  display: none;
  flex-direction: column;
  gap: 8px;
  @media (max-width: 767px) {
    display: flex;
  }
`;

const MobileCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid #eaebea;
`;

const MobileCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MobileCardCompanyName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #2f382c;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobileCardStatus = styled.span<{ status: string }>`
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ status }) => {
    if (status === 'SUBMITTED') return '#136FD8';
    if (status === 'VERIFIED') return '#2998BD';
    if (status === 'PENDING') return '#E56910';
    if (status === 'QUALIFIED') return '#994EFE';
    if (status === 'REWARDED') return '#43A047';
    if (status === 'REJECTED') return '#E2483C';
    if (status === 'ORDERED') return '#D48806';
    return '#2f382c';
  }};
`;

const MobileDivider = styled.div`
  height: 1px;
  background: #eaebea;
`;

const MobileCardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MobileCardInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  justify-content: space-between;
`;

const MobileCardLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #596056;
  margin-right: 4px;
`;

const MobileCardValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #2f382c;
`;

const StatusText = styled.span<{ status: string }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ status }) => {
    if (status === 'SUBMITTED') return '#0D66F6';
    if (status === 'VERIFIED') return '#2998BD';
    if (status === 'PENDING') return '#E56910';
    if (status === 'QUALIFIED') return '#D1A91E';
    if (status === 'REWARDED') return '#3C9041';
    if (status === 'REJECTED') return '#E2483C';
    return '#2f382c';
  }};
`;

interface StateProps {}

const STEPS = [
  { title: messages.instructorStep1, desc: messages.instructorStep1Description },
  { title: messages.instructorStep2, desc: messages.instructorStep2Description },
  { title: messages.instructorStep3, desc: messages.instructorStep3Description },
  { title: messages.instructorStep4, desc: messages.instructorStep4Description },
  { title: messages.instructorStep5, desc: messages.instructorStep5Description },
];

const Referral = ({ buyer }) => {
  const [openModal, setOpenModal] = useState(false);
  const { data } = useQuerySaga<GQuery<'referralContactRequestHistory'>>(referralContactRequestHistory);

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success(translations(messages.copySuccessfully));
    } catch (err) {
      console.error('Copy failed', err);
    }
  };
  const referralLink = `${process.env.APP_URL}/contact-us?referralCode=${buyer.refCode}`;

  const handleShare = () => {
    if (!navigator.share) {
      navigator.clipboard.writeText(referralLink).catch(() => {});
      message.success(translations(messages.copySuccessfully));
      return;
    }
    navigator
      .share({
        title: 'Kamereo Referral',
        text: 'Giới thiệu bạn bè để nhận voucher',
        url: referralLink,
      })
      .catch((err) => {
        if (err instanceof Error && err.name !== 'AbortError') {
          navigator.clipboard.writeText(referralLink).catch(() => {});
          message.success(translations(messages.copySuccessfully));
        }
      });
  };
  return (
    <PageWrapper>
      <TitleText>
        <FormattedMessage {...messages.header} />
      </TitleText>

      <HeaderCard>
        <HeaderIconBox>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M20 8H4C3.44772 8 3 8.44772 3 9V11C3 11.5523 3.44772 12 4 12H20C20.5523 12 21 11.5523 21 11V9C21 8.44772 20.5523 8 20 8Z"
              stroke="white"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path d="M12 8V21" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path
              d="M19 12V19C19 19.5304 18.7893 20.0391 18.4142 20.4142C18.0391 20.7893 17.5304 21 17 21H7C6.46957 21 5.96086 20.7893 5.58579 20.4142C5.21071 20.0391 5 19.5304 5 19V12"
              stroke="white"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M7.5 8.00044C6.83696 8.00044 6.20107 7.73705 5.73223 7.2682C5.26339 6.79936 5 6.16348 5 5.50044C5 4.8374 5.26339 4.20151 5.73223 3.73267C6.20107 3.26383 6.83696 3.00044 7.5 3.00044C8.46469 2.98363 9.41003 3.4517 10.2127 4.3436C11.0154 5.2355 11.6383 6.50984 12 8.00044C12.3617 6.50984 12.9846 5.2355 13.7873 4.3436C14.59 3.4517 15.5353 2.98363 16.5 3.00044C17.163 3.00044 17.7989 3.26383 18.2678 3.73267C18.7366 4.20151 19 4.8374 19 5.50044C19 6.16348 18.7366 6.79936 18.2678 7.2682C17.7989 7.73705 17.163 8.00044 16.5 8.00044"
              stroke="white"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </HeaderIconBox>

        <HeaderMainText>
          <FormattedMessage {...messages.description} />
        </HeaderMainText>
        <HeaderSubText>
          <FormattedMessage {...messages.subDescription} />{' '}
          <TextVsLink to={'/referral-program'}>
            <FormattedMessage {...messages.viewDetail} />
          </TextVsLink>{' '}
          {translations(messages.or)}{' '}
          <TextLink
            onClick={() =>
              window.open(
                'https://image-handler-prod.s3.ap-southeast-1.amazonaws.com/supplier/654/FILE/Copy+of+Term+%26+Condition+Referral+3.0.pdf',
                '_blank',
              )
            }
          >
            <FormattedMessage {...messages.termPolicy} />
          </TextLink>
          .
        </HeaderSubText>
      </HeaderCard>

      <BottomSection>
        <Card>
          <CardTitle>{translations(messages.yourReferralCode).toUpperCase()}</CardTitle>

          <CodeSection>
            <CodeRow>
              <CodeLabel>
                <FormattedMessage {...messages.referralCodeLabel} />
              </CodeLabel>
              <CopyRow>
                <CodeBox>{buyer.refCode}</CodeBox>
                <CopyButton onClick={() => handleCopy(buyer.refCode)}>
                  <SVGIcon src={CopyIcon} />
                  <span>
                    <FormattedMessage {...messages.copy} />
                  </span>
                </CopyButton>
              </CopyRow>
            </CodeRow>
            <CodeRow>
              <CodeLabel>
                <FormattedMessage {...messages.orShareLink} />
              </CodeLabel>
              <CopyRow>
                <CodeBox>
                  <div style={{ display: 'flex' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M8 6C8.27614 6 8.5 6.22386 8.5 6.5C8.5 6.74546 8.32312 6.94961 8.08988 6.99194L8 7H6C4.34315 7 3 8.34315 3 10C3 11.5906 4.23784 12.892 5.80275 12.9936L6 13H8C8.27614 13 8.5 13.2239 8.5 13.5C8.5 13.7455 8.32312 13.9496 8.08988 13.9919L8 14H6C3.79086 14 2 12.2091 2 10C2 7.8645 3.67346 6.11986 5.78053 6.00592L6 6H8ZM14 6C16.2091 6 18 7.79086 18 10C18 12.1355 16.3265 13.8801 14.2195 13.9941L14 14H12C11.7239 14 11.5 13.7761 11.5 13.5C11.5 13.2545 11.6769 13.0504 11.9101 13.0081L12 13H14C15.6569 13 17 11.6569 17 10C17 8.40942 15.7622 7.10795 14.1973 7.00638L14 7H12C11.7239 7 11.5 6.77614 11.5 6.5C11.5 6.25454 11.6769 6.05039 11.9101 6.00806L12 6H14ZM6 9.5H14C14.2761 9.5 14.5 9.72386 14.5 10C14.5 10.2455 14.3231 10.4496 14.0899 10.4919L14 10.5H6C5.72386 10.5 5.5 10.2761 5.5 10C5.5 9.75454 5.67688 9.55039 5.91012 9.50806L6 9.5H14H6Z"
                        fill="#2F382C"
                      />
                    </svg>
                  </div>
                  <ReferralLink>{referralLink}</ReferralLink>
                </CodeBox>
                <CopyButton onClick={() => handleCopy(referralLink)}>
                  <SVGIcon src={CopyIcon} />
                  <span>
                    <FormattedMessage {...messages.copy} />
                  </span>
                </CopyButton>
              </CopyRow>
            </CodeRow>
            {/* <ShareButton type="default" onClick={handleShare}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6.74609 4.00003H10.2103C10.6245 4.00003 10.9603 4.33582 10.9603 4.75003C10.9603 5.12972 10.6781 5.44352 10.3121 5.49318L10.2103 5.50003H6.74609C5.55523 5.50003 4.58045 6.42519 4.50128 7.59598L4.49609 7.75003V17.25C4.49609 18.4409 5.42126 19.4157 6.59204 19.4948L6.74609 19.5H16.2468C17.4377 19.5 18.4125 18.5749 18.4916 17.4041L18.4968 17.25V16.7522C18.4968 16.338 18.8326 16.0022 19.2468 16.0022C19.6265 16.0022 19.9403 16.2844 19.99 16.6505L19.9968 16.7522V17.25C19.9968 19.2543 18.4245 20.8913 16.446 20.9948L16.2468 21H6.74609C4.74183 21 3.10482 19.4277 3.00129 17.4492L2.99609 17.25V7.75003C2.99609 5.74577 4.56845 4.10876 6.54694 4.00523L6.74609 4.00003H10.2103H6.74609ZM14.5001 6.51988V3.75003C14.5001 3.12606 15.2069 2.78998 15.6871 3.13983L15.7693 3.20877L21.7639 8.95877C22.0436 9.22712 22.0691 9.65814 21.8402 9.9561L21.7639 10.0412L15.7693 15.7931C15.3191 16.2251 14.5872 15.9477 14.5072 15.3589L14.5001 15.2519V12.5266L14.1566 12.5567C11.7569 12.807 9.45687 13.8879 7.24204 15.8174C6.72293 16.2696 5.9198 15.842 6.00518 15.1589C6.66997 9.83933 9.45184 6.90733 14.2007 6.53953L14.5001 6.51988V3.75003V6.51988ZM16.0001 5.50867V7.25003C16.0001 7.66424 15.6643 8.00003 15.2501 8.00003C11.3767 8.00003 8.97606 9.67616 7.93882 13.1572L7.85976 13.4358L8.21195 13.199C10.4484 11.7372 12.7978 11 15.2501 11C15.6298 11 15.9436 11.2822 15.9932 11.6483L16.0001 11.75V13.4928L20.1613 9.50012L16.0001 5.50867Z"
                  fill="#343538"
                />
              </svg>
              <FormattedMessage {...messages.yourReferralCode} />
            </ShareButton> */}
          </CodeSection>
        </Card>

        <Card>
          <CardTitle>{translations(messages.instructor).toUpperCase()}</CardTitle>

          <StepList>
            {STEPS.map((step, index) => (
              <StepItem key={index}>
                <StepBadge>{index + 1}</StepBadge>
                <StepContentGroup>
                  <StepTitle>
                    <FormattedMessage {...step.title} />
                  </StepTitle>
                  <StepDescription>
                    <FormattedMessage {...step.desc} />
                  </StepDescription>
                </StepContentGroup>
              </StepItem>
            ))}
          </StepList>
        </Card>
      </BottomSection>

      <ReferralListCard>
        <ReferralListTitle>{translations(messages.referralListTitle).toUpperCase()}</ReferralListTitle>
        <StyledTable
          dataSource={(data?.data as ReferralContactRequest[]) || []}
          rowKey="id"
          pagination={false}
          scroll={{ x: 700 }}
          columns={[
            {
              title: translations(messages.colCompanyName),
              dataIndex: 'name',
              key: 'name',
              render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
            },
            {
              title: translations(messages.colRegistrationDate),
              dataIndex: 'registeredAt',
              key: 'registeredAt',
              render: (val) => formDateTimeFromGraphQL(val) || '--',
            },
            {
              title: translations(messages.colStatus),
              dataIndex: 'status',
              key: 'status',
              render: (status) => (
                <StatusText status={status}>{status ? translations(messages[status]) : '--'}</StatusText>
              ),
            },
            {
              title: translations(messages.colVoucherValue),
              dataIndex: 'totalVoucherAmount',
              key: 'totalVoucherAmount',
              align: 'right',
              render: (val) => (Number(val) ? formatMoney(Number(val)) : '--'),
            },
          ]}
        />
        <MobileList>
          {((data?.data as ReferralContactRequest[]) || []).map((item: any) => (
            <MobileCard key={item.id}>
              <MobileCardHeader>
                <MobileCardCompanyName>{item.name}</MobileCardCompanyName>
                <MobileCardStatus status={item?.status}>
                  {item?.status ? translations(messages[item?.status]) : '--'}
                </MobileCardStatus>
              </MobileCardHeader>
              <MobileDivider />
              <MobileCardInfo>
                <MobileCardInfoRow>
                  <MobileCardLabel>
                    <FormattedMessage {...messages.colRegistrationDate} />
                  </MobileCardLabel>
                  <MobileCardValue>{formDateTimeFromGraphQL(item.registeredAt) || '--'}</MobileCardValue>
                </MobileCardInfoRow>
                <MobileCardInfoRow>
                  <MobileCardLabel>
                    <FormattedMessage {...messages.colVoucherValue} />
                  </MobileCardLabel>
                  <MobileCardValue>
                    {Number(item?.totalVoucherAmount) ? formatMoney(Number(item?.totalVoucherAmount)) : '--'}
                  </MobileCardValue>
                </MobileCardInfoRow>
              </MobileCardInfo>
            </MobileCard>
          ))}
        </MobileList>
      </ReferralListCard>

      <PolicyModal open={openModal} setOpen={setOpenModal} />
    </PageWrapper>
  );
};

const mapStateToProps = createStructuredSelector<ApplicationRootState, StateProps>({
  buyer: selectBuyer(),
});

const withConnect = connect(mapStateToProps, () => {});

export default withConnect(Referral);
